<template>
  <div class="familias-manager">
    
    <!-- Header y Controles -->
    <div class="manager-header">
      <div class="row align-items-center">
        <div class="col-md-6">
          <h4 class="manager-title">
            <i class="nc-icon nc-folder-17 mr-2"></i>
            Gestión de Familias
          </h4>
          <small class="text-muted">
            Crear, editar y organizar las familias de planes del sistema
          </small>
        </div>
        <div class="col-md-6 text-right">
          <button 
            class="btn btn-primary btn-sm mr-2" 
            @click="abrirModalCrear"
            :disabled="loading.crear || loading.guardar"
            type="button"
          >
            <span v-if="loading.crear || loading.guardar" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-simple-add mr-1"></i>
            Nueva Familia
          </button>
          <button 
            class="btn btn-outline-secondary btn-sm" 
            @click="refreshFamilias"
            :disabled="loading.tabla || loading.refrescar"
          >
            <span v-if="loading.refrescar" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-refresh-02 mr-1"></i>
            <span class="d-none d-sm-inline">Refrescar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Filtros y Búsqueda -->
    <div class="filters-section mb-4">
      <div class="card">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <div class="col-md-4">
              <div class="form-group mb-0">
                <label class="form-label">Buscar Familia</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm"
                  v-model="filtros.nombre"
                  @input="debounceFilter"
                  placeholder="Nombre de la familia..."
                >
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Estado</label>
                <select 
                  class="form-control form-control-sm"
                  v-model="filtros.activo"
                  @change="aplicarFiltros"
                >
                  <option value="">Todos</option>
                  <option value="true">Activos</option>
                  <option value="false">Inactivos</option>
                </select>
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group mb-0">
                <label class="form-label">Fecha Creación</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm"
                  v-model="filtros.fechaCreacion"
                  @change="aplicarFiltros"
                  placeholder="Buscar desde fecha..."
                  title="Filtrar familias creadas desde esta fecha"
                >
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group mb-0">
                <label class="form-label">&nbsp;</label>
                <button 
                  class="btn btn-outline-secondary btn-sm btn-block"
                  @click="limpiarFiltros"
                >
                  <i class="nc-icon nc-simple-remove"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Familias -->
    <div class="table-section">
      <div class="card">
        <div class="card-header">
          <div class="row align-items-center">
            <div class="col">
              <h6 class="card-title mb-0">
                Lista de Familias
                <span class="badge badge-secondary ml-2" v-if="!loading.tabla">
                  {{ pagination.totalRecords }} registros
                </span>
              </h6>
            </div>
          </div>
        </div>
        
        <div class="card-body p-0">
          <!-- Loading -->
          <div v-if="loading.tabla" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando familias...</span>
            </div>
            <p class="mt-3 text-muted">Cargando familias...</p>
          </div>

          <!-- Tabla -->
          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>
                    <button class="btn-sort" @click="sortBy('id')">
                      ID 
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>
                    <button class="btn-sort" @click="sortBy('nombre')">
                      Nombre
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'nombre' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'nombre' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>
                    <button class="btn-sort" @click="sortBy('createdAt')">
                      Fecha Creación
                      <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'createdAt' && ordenamiento.direccion === 'asc'"></i>
                      <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'createdAt' && ordenamiento.direccion === 'desc'"></i>
                    </button>
                  </th>
                  <th>Estado</th>
                  <th>Usuario Creación</th>
                  <th width="160">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="familia in familias" :key="familia._id">
                  <td>
                    <span class="badge badge-outline-primary">{{ familia.id }}</span>
                  </td>
                  <td>
                    <div class="familia-info">
                      <strong 
                        class="familia-nombre-clickeable" 
                        @click="navegarASubFamilias(familia)"
                        :title="'Ver subfamilias de ' + familia.nombre"
                      >
                        {{ familia.nombre }}
                      </strong>
                      <br>
                      <small class="text-muted" v-if="familia.estadisticas">
                        <span 
                          class="subfamilias-link" 
                          @click="navegarASubFamilias(familia)"
                          :title="'Ver subfamilias'"
                        >
                          {{ familia.estadisticas.subfamiliasVinculadas }} subfamilias
                        </span>, 
                        {{ familia.estadisticas.planesVinculados }} planes
                      </small>
                    </div>
                  </td>
                  <td>
                    <small>{{ familia.fechaCreacionFormatted || formatearFecha(familia.createdAt) }}</small>
                  </td>
                  <td>
                    <span 
                      class="badge"
                      :class="familia.activo ? 'badge-success' : 'badge-secondary'"
                    >
                      {{ familia.activo ? 'Activo' : 'Inactivo' }}
                    </span>
                  </td>
                  <td>
                    <small>{{ familia.nombreUsuarioCreacion }}</small>
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-success"
                        @click="navegarASubFamilias(familia)"
                        :title="'Ver subfamilias de ' + familia.nombre"
                      >
                        <i class="nc-icon nc-settings-gear-65"></i>
                      </button>
                      <button 
                        class="btn btn-outline-info"
                        @click="verDetalle(familia)"
                        :disabled="loading.verDetalle === familia.id"
                        :title="'Ver detalles de ' + familia.nombre"
                      >
                        <span v-if="loading.verDetalle === familia.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-zoom-split"></i>
                      </button>
                      <button 
                        class="btn btn-outline-primary"
                        @click="editarFamilia(familia)"
                        :disabled="loading.editar === familia.id"
                        :title="'Editar ' + familia.nombre"
                      >
                        <span v-if="loading.editar === familia.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-ruler-pencil"></i>
                      </button>
                      <button 
                        class="btn btn-outline-danger"
                        @click="confirmarEliminar(familia)"
                        :title="familia.activo ? 'Eliminar (Etapa 1): ' + familia.nombre : 'Eliminar Permanentemente (Etapa 2): ' + familia.nombre"
                        :disabled="loading.eliminar === familia.id"
                      >
                        <span v-if="loading.eliminar === familia.id" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="nc-icon nc-simple-remove"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Sin Resultados -->
          <div v-if="!loading.tabla && familias.length === 0" class="empty-state text-center py-5">
            <i class="nc-icon nc-folder-17 empty-icon"></i>
            <h5>No hay familias</h5>
            <p class="text-muted">
              No se encontraron familias con los filtros aplicados.
            </p>
            <button class="btn btn-primary" @click="abrirModalCrear">
              <i class="nc-icon nc-simple-add mr-1"></i>
              Crear primera familia
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginación -->
    <div v-if="pagination.totalPages > 1" class="pagination-section mt-4">
      <nav>
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: !pagination.hasPrev }">
            <button 
              class="page-link" 
              @click="cambiarPagina(pagination.prevPage)"
              :disabled="!pagination.hasPrev"
            >
              <i class="nc-icon nc-minimal-left"></i>
            </button>
          </li>
          
          <li 
            v-for="pagina in paginasVisibles" 
            :key="pagina"
            class="page-item" 
            :class="{ active: pagina === pagination.page }"
          >
            <button class="page-link" @click="cambiarPagina(pagina)">
              {{ pagina }}
            </button>
          </li>
          
          <li class="page-item" :class="{ disabled: !pagination.hasNext }">
            <button 
              class="page-link" 
              @click="cambiarPagina(pagination.nextPage)"
              :disabled="!pagination.hasNext"
            >
              <i class="nc-icon nc-minimal-right"></i>
            </button>
          </li>
        </ul>
      </nav>
      
      <div class="pagination-info text-center mt-2">
        <small class="text-muted">
          Mostrando {{ (pagination.page - 1) * pagination.limit + 1 }} a 
          {{ Math.min(pagination.page * pagination.limit, pagination.totalRecords) }} 
          de {{ pagination.totalRecords }} registros
        </small>
      </div>
    </div>

    <!-- Modal Crear/Editar Familia -->
    <div class="modal fade" ref="modalFamilia" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="nc-icon nc-folder-17 mr-2"></i>
              {{ modoEdicion ? 'Editar Familia' : 'Nueva Familia' }}
            </h5>
            <button type="button" class="close" @click="cerrarModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div class="modal-body">
            <form @submit.prevent="guardarFamilia">
              <div class="form-group">
                <label for="nombreFamilia" class="form-label">
                  Nombre de la Familia <span class="text-danger">*</span>
                </label>
                <input 
                  type="text" 
                  class="form-control"
                  id="nombreFamilia"
                  v-model="formFamilia.nombre"
                  placeholder="Ej: Adulto, Juvenil, Premium..."
                  maxlength="200"
                  required
                  :disabled="loading.guardar"
                >
                <small class="form-text text-muted">
                  Máximo 200 caracteres. Este nombre se usará para clasificar los planes.
                </small>
              </div>
              
              <!-- Campo solo visible en modo edición -->
              <div v-if="modoEdicion" class="form-group">
                <div class="custom-control custom-switch">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="actualizarPlanes"
                    v-model="formFamilia.actualizarPlanes"
                    :disabled="loading.guardar"
                  >
                  <label class="custom-control-label" for="actualizarPlanes">
                    <strong>Actualizar Planes en Cascada</strong>
                  </label>
                </div>
                <small class="form-text text-muted">
                  <i class="nc-icon nc-alert-circle-i mr-1"></i>
                  Si está activo, se actualizará el nombre de la familia en todos los planes asociados.
                </small>
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="cerrarModal"
              :disabled="loading.guardar"
            >
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="guardarFamilia"
              :disabled="loading.guardar || !formFamilia.nombre.trim()"
            >
              <span v-if="loading.guardar" class="spinner-border spinner-border-sm mr-2"></span>
              {{ modoEdicion ? 'Actualizar' : 'Crear' }} Familia
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'FamiliasManager',
  props: {
    catalogos: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: {
        tabla: false,
        crear: false,
        editar: null,
        guardar: false,
        eliminar: null,
        verDetalle: null,
        exportar: false,
        refrescar: false
      },
      familias: [],
      filtros: {
        nombre: '',
        activo: '',
        fechaCreacion: ''
      },
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false,
        nextPage: null,
        prevPage: null
      },
      ordenamiento: {
        campo: 'createdAt',
        direccion: 'desc'
      },
      // Modal y formulario
      modoEdicion: false,
      familiaSeleccionada: null,
      formFamilia: {
        nombre: '',
        actualizarPlanes: false
      },
      // Debounce
      debounceTimer: null
    }
  },
  computed: {
    paginasVisibles() {
      const total = this.pagination.totalPages
      const actual = this.pagination.page
      const visibles = []
      
      let inicio = Math.max(1, actual - 2)
      let fin = Math.min(total, actual + 2)
      
      for (let i = inicio; i <= fin; i++) {
        visibles.push(i)
      }
      
      return visibles
    }
  },
  async mounted() {
    await this.cargarFamilias()
  },
  methods: {
    async cargarFamilias() {
      try {
        this.loading.tabla = true
        console.log('📊 Cargando familias con filtros:', this.getFiltrosActivos())
        
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: {
              Operacion: '/v1/planes/familia/consultar-paginadas'
            },
            Auth: {
              Token: sessionStorage.getItem('jCxbraoQeD')
            },
            Parameters: {
              page: this.pagination.page,
              limit: this.pagination.limit,
              sortBy: this.ordenamiento.campo,
              sortOrder: this.ordenamiento.direccion,
              ...this.getFiltrosActivos()
            },
            Body: {}
          }
        })

        console.log('📊 Respuesta familias:', response.data)

        if (response.data && response.data.dataOut && response.data.dataOut.familias) {
          this.familias = response.data.dataOut.familias
          this.pagination = response.data.dataOut.pagination || this.pagination
          
          // Emitir estadísticas actualizadas
          this.$emit('actualizarEstadisticas', {
            totalFamilias: this.pagination.totalRecords
          })
          
          console.log('✅ Familias cargadas:', this.familias.length)
        } else {
          console.warn('⚠️ No se encontraron familias en la respuesta')
          this.familias = []
        }
        
      } catch (error) {
        console.error('❌ Error al cargar familias:', error)
        
        // Manejo específico de errores
        let errorMessage = 'No se pudieron cargar las familias'
        
        if (error.response && error.response.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response && error.response.status === 400) {
          errorMessage = error.response.data && error.response.data.result && error.response.data.result.message || 'Error en los parámetros de la consulta'
        } else if (error.response && error.response.status === 500) {
          errorMessage = 'Error interno del servidor. Inténtalo nuevamente.'
        }
        
        this.$swal.fire({
          title: 'Error al Cargar Familias',
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 4000,
          showConfirmButton: false
        })
        
        this.familias = []
      } finally {
        this.loading.tabla = false
      }
    },

    navegarASubFamilias(familia) {
      console.log('🔄 Navegando a SubFamilias con familia:', familia.nombre, 'Estado:', familia.activo ? 'Activa' : 'Inactiva')
      
      // Emitir evento al componente padre para cambiar de pestaña
      this.$emit('navegarASubFamilias', {
        familiaId: familia._id,  // MongoDB _id para la consulta API
        familiaIdNumerico: familia.id,  // ID numérico para display
        familiaNombre: familia.nombre,
        familiaActivo: familia.activo,  // Estado de la familia
        tab: 'subfamilias'
      })
      
      // Mostrar confirmación
      this.$swal.fire({
        title: 'Navegando...',
        text: `Accediendo a las subfamilias de "${familia.nombre}" ${familia.activo ? '(Activa)' : '(Inactiva)'}`,
        icon: 'info',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    },

    getFiltrosActivos() {
      const filtros = {}
      
      if (this.filtros.nombre.trim()) {
        filtros.nombre = this.filtros.nombre.trim()
      }
      
      // Solo incluir parámetro activo si se ha seleccionado explícitamente un valor
      // Si no se incluye, la API devuelve todas las familias (activas e inactivas)
      if (this.filtros.activo !== '') {
        filtros.activo = this.filtros.activo === 'true'
      }
      
      if (this.filtros.fechaCreacion) {
        filtros.fechaCreacion = this.filtros.fechaCreacion
      }
      
      return filtros
    },

    debounceFilter() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.aplicarFiltros()
      }, 500)
    },

    async aplicarFiltros() {
      this.pagination.page = 1
      await this.cargarFamilias()
    },

    limpiarFiltros() {
      this.filtros = {
        nombre: '',
        activo: '',
        fechaCreacion: ''
      }
      this.aplicarFiltros()
    },

    async refreshFamilias() {
      try {
        this.loading.refrescar = true
        await this.cargarFamilias()
        this.$emit('refresh')
        
        // Mostrar confirmación breve
        this.$swal.fire({
          title: '¡Actualizado!',
          text: 'Lista de familias refrescada exitosamente',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-success',
          allowOutsideClick: false,
          allowEscapeKey: false
        })
      } catch (error) {
        console.error('Error al refrescar:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo refrescar la lista de familias',
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false
        })
      } finally {
        this.loading.refrescar = false
      }
    },

    async sortBy(campo) {
      if (this.ordenamiento.campo === campo) {
        this.ordenamiento.direccion = this.ordenamiento.direccion === 'asc' ? 'desc' : 'asc'
      } else {
        this.ordenamiento.campo = campo
        this.ordenamiento.direccion = 'asc'
      }
      
      await this.cargarFamilias()
    },

    async cambiarPagina(pagina) {
      if (pagina && pagina !== this.pagination.page) {
        this.pagination.page = pagina
        await this.cargarFamilias()
      }
    },

    // CRUD Operations
    abrirModalCrear() {
      console.log('🎯 Abriendo modal para crear nueva familia')
      
      this.modoEdicion = false
      this.familiaSeleccionada = null
      this.formFamilia = {
        nombre: '',
        actualizarPlanes: false
      }
      
      // Pequeño delay para asegurar que el DOM está listo
      this.$nextTick(() => {
        this.mostrarModal()
      })
    },

    editarFamilia(familia) {
      this.loading.editar = familia.id
      
      this.modoEdicion = true
      this.familiaSeleccionada = familia
      this.formFamilia = {
        nombre: familia.nombre,
        actualizarPlanes: false
      }
      
      this.$nextTick(() => {
        this.mostrarModal()
        this.loading.editar = null
      })
    },

    async guardarFamilia() {
      if (!this.formFamilia.nombre.trim()) {
        this.$swal.fire({
          title: 'Error',
          text: 'El nombre de la familia es requerido',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-primary'
        })
        return
      }

      try {
        this.loading.guardar = true

        console.log('📝 Guardando familia:', {
          modo: this.modoEdicion ? 'edición' : 'creación',
          nombre: this.formFamilia.nombre.trim(),
          actualizarPlanes: this.modoEdicion ? this.formFamilia.actualizarPlanes : 'N/A'
        })

        const operacion = this.modoEdicion ? '/v1/planes/familia/actualizar' : '/v1/planes/familia/crear'
        
        // Mapeo correcto de campos según especificación del endpoint
        let parameters = {}
        let body = {}
        
        if (this.modoEdicion) {
          // Modo edición: Parameters contiene el ID de la familia
          parameters = {
            id: this.familiaSeleccionada.id
          }
          // Body contiene nuevo nombre y bandera de actualización en cascada
          body = {
            nombre: this.formFamilia.nombre.trim(),
            actualizarPlanes: this.formFamilia.actualizarPlanes || false
          }
        } else {
          // Modo creación: Parameters vacío
          parameters = {}
          // Body contiene solo el nombre
          body = {
            nombre: this.formFamilia.nombre.trim()
          }
        }

        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: operacion },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: parameters,
            Body: body
          }
        })

        console.log('📝 Respuesta del servidor:', response.data)

        // Verificar respuesta exitosa según la especificación
        if (response.data?.result?.code === 0 || response.data?.result?.httpCode === 201 || response.data?.result?.httpCode === 200) {
          const familiaCreada = response.data?.dataOut?.familia
          const mensaje = response.data?.dataOut?.mensaje || `Familia ${this.modoEdicion ? 'actualizada' : 'creada'} exitosamente`
          const actualizacionCascada = response.data?.dataOut?.actualizacionCascada
          
          // Construir información adicional sobre actualización en cascada
          let infoCascada = ''
          if (this.modoEdicion && actualizacionCascada) {
            const planesActualizados = actualizacionCascada.planesActualizados || 0
            const mensajeCascada = actualizacionCascada.mensaje || ''
            
            if (planesActualizados > 0) {
              infoCascada = `
                <div class="alert alert-info text-left" style="margin: 15px 0; background-color: #d1ecf1; border-color: #17a2b8; color: #0c5460;">
                  <strong style="color: #0c5460;">🔄 Actualización en Cascada:</strong>
                  <ul style="margin: 5px 0 0 20px; color: #0c5460;">
                    <li><strong>${planesActualizados} plan${planesActualizados > 1 ? 'es' : ''}</strong> actualizado${planesActualizados > 1 ? 's' : ''} con el nuevo nombre</li>
                  </ul>
                  <small style="color: #0c5460; opacity: 0.8;">${mensajeCascada}</small>
                </div>
              `
            } else if (this.formFamilia.actualizarPlanes) {
              infoCascada = `
                <div class="alert alert-warning text-left" style="margin: 15px 0; background-color: #fff3cd; border-color: #ffc107; color: #856404;">
                  <strong style="color: #856404;">ℹ️ Sin planes vinculados:</strong>
                  <p style="margin: 5px 0 0 0; font-size: 0.9rem; color: #856404;">No se encontraron planes asociados a esta familia para actualizar.</p>
                </div>
              `
            }
          }
          
          this.$swal.fire({
            title: '¡Éxito!',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>
                <h5 style="color: #28a745; margin-bottom: 15px;">¡Familia ${this.modoEdicion ? 'Actualizada' : 'Creada'}!</h5>
                
                <div class="alert alert-success text-left" style="margin: 15px 0;">
                  <strong>✅ ${this.modoEdicion ? 'Familia actualizada:' : 'Nueva familia creada:'}</strong>
                  <ul style="margin: 5px 0 0 20px;">
                    <li><strong>Nombre:</strong> "${familiaCreada ? familiaCreada.nombre : this.formFamilia.nombre}"</li>
                    <li><strong>ID:</strong> ${familiaCreada ? familiaCreada.id : 'N/A'}</li>
                  </ul>
                </div>
                
                ${infoCascada}
                
                <p class="text-muted" style="font-size: 0.9rem;">
                  ${mensaje}
                </p>
              </div>
            `,
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-success',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              container: 'swal-familia-exitosa'
            }
          })

          this.cerrarModal()
          await this.cargarFamilias()
          
          // Emitir evento para actualizar estadísticas
          this.$emit('refresh')
          
        } else if (response.data?.result?.code === 400) {
          // Error de validación general
          const errorMessage = response.data.result.message || 
                             response.data.result.errors?.[0] || 
                             'Error al procesar la solicitud'
          
          this.$swal.fire({
            title: 'Error de Validación',
            text: errorMessage,
            icon: 'warning',
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-warning'
          })
        } else if (response.data?.result?.code === 409) {
          // Error específico: Familia ya existe (Conflict)
          const errorMessage = response.data.result.message || 
                             response.data.result.errors?.[0] || 
                             'Ya existe una familia con este nombre'
          
          this.$swal.fire({
            title: 'Familia Duplicada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #f39c12; margin-bottom: 15px;"></i>
                <p style="font-size: 1.1rem; margin-bottom: 15px;">
                  <strong>${errorMessage}</strong>
                </p>
                <p class="text-muted" style="font-size: 0.9rem;">
                  Por favor, elige un nombre diferente para la familia o edita la familia existente.
                </p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'Cambiar Nombre',
            confirmButtonClass: 'btn btn-warning',
            allowOutsideClick: false,
            customClass: {
              container: 'swal-familia-duplicada'
            }
          }).then(() => {
            // Mantener el modal abierto y enfocar el campo de nombre
            this.$nextTick(() => {
              const inputNombre = document.getElementById('nombreFamilia')
              if (inputNombre) {
                inputNombre.focus()
                inputNombre.select()
              }
            })
          })
          
          // No cerrar el modal para que el usuario pueda corregir el nombre
          return
          
        } else {
          // Otros errores
          throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
        }

      } catch (error) {
        console.error('❌ Error al guardar familia:', error)
        console.log('📊 Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })
        
        let errorMessage = 'No se pudo guardar la familia'
        let errorTitle = 'Error'
        
        if (error.response?.status === 400) {
          errorMessage = error.response.data?.result?.message || 
                        error.response.data?.result?.errors?.[0] ||
                        'Error de validación en los datos enviados'
          errorTitle = 'Error de Validación'
        } else if (error.response?.status === 409) {
          // Manejo específico para conflicto (familia duplicada)
          const serverMessage = error.response.data?.result?.message || 
                               error.response.data?.result?.errors?.[0] ||
                               'Ya existe una familia con este nombre'
          
          this.$swal.fire({
            title: 'Familia Duplicada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #f39c12; margin-bottom: 15px;"></i>
                <p style="font-size: 1.1rem; margin-bottom: 15px;">
                  <strong>${serverMessage}</strong>
                </p>
                <p class="text-muted" style="font-size: 0.9rem;">
                  Por favor, elige un nombre diferente para la familia o edita la familia existente.
                </p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'Cambiar Nombre',
            confirmButtonClass: 'btn btn-warning',
            allowOutsideClick: false
          }).then(() => {
            // Mantener el modal abierto y enfocar el campo de nombre
            this.$nextTick(() => {
              const inputNombre = document.getElementById('nombreFamilia')
              if (inputNombre) {
                inputNombre.focus()
                inputNombre.select()
              }
            })
          })
          
          // Salir del catch sin mostrar el modal de error genérico
          return
          
        } else if (error.response?.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          errorTitle = 'Sesión Expirada'
          // Opcional: redirigir al login
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response?.status >= 500) {
          errorMessage = 'Error interno del servidor. Por favor, intenta nuevamente en unos momentos.'
          errorTitle = 'Error del Servidor'
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.loading.guardar = false
      }
    },

    async confirmarEliminar(familia) {
      // Activar loading inmediatamente cuando se hace clic en eliminar
      this.loading.eliminar = familia.id

      // Primero consultar información de la familia para conocer dependencias
      let subfamiliasVinculadas = 0
      let planesVinculados = 0
      let planesActivosDetalle = []
      
      try {
        // Consultar detalle de la familia para obtener estadísticas
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: '/v1/planes/familia/consultar-por-id' },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: { id: familia.id },
            Body: {}
          }
        })

        if (response.data?.dataOut?.familia?.estadisticas) {
          subfamiliasVinculadas = response.data.dataOut.familia.estadisticas.subfamiliasVinculadas || 0
          planesVinculados = response.data.dataOut.familia.estadisticas.planesVinculados || 0
        }
        
        // Si es una familia inactiva (etapa 2), consultar planes activos específicos
        if (!familia.activo && planesVinculados > 0) {
          try {
            const testResponse = await this.$CentralGymOPS_API({
              method: 'POST',
              url: '/',
              headers: {
                'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
              },
              data: {
                Header: { Operacion: '/v1/planes/familia/eliminar' },
                Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
                Parameters: { id: familia.id },
                Body: {}
              }
            })
          } catch (testError) {
            // Si el error es 409, extraer los planes activos
            if (testError.response?.status === 409 && testError.response.data?.dataOut?.planesActivos) {
              planesActivosDetalle = testError.response.data.dataOut.planesActivos || []
              console.log('📋 Planes activos obtenidos para confirmación:', planesActivosDetalle)
            }
          }
        }
        
      } catch (error) {
        console.warn('No se pudieron obtener estadísticas de la familia:', error)
        // Continuar con la eliminación aunque no tengamos estadísticas
      }

      // Crear mensaje dinámico basado en las dependencias
      let mensajeSubfamilias = ''
      let mensajePlanes = ''
      let listaPlanesDetalles = ''
      
      if (subfamiliasVinculadas > 0) {
        mensajeSubfamilias = `<li><strong>${subfamiliasVinculadas} subfamilia${subfamiliasVinculadas > 1 ? 's' : ''}</strong> también ${subfamiliasVinculadas > 1 ? 'serán desactivadas' : 'será desactivada'}</li>`
      }
      
      if (planesVinculados > 0) {
        mensajePlanes = `<li><strong>${planesVinculados} plan${planesVinculados > 1 ? 'es' : ''}</strong> puede${planesVinculados > 1 ? 'n' : ''} verse afectado${planesVinculados > 1 ? 's' : ''}</li>`
      }
      
              // Si tenemos planes activos detallados (familia inactiva - etapa 2), construir la lista
        if (planesActivosDetalle.length > 0) {
          const planesParaMostrar = planesActivosDetalle.slice(0, 5)
          planesParaMostrar.forEach(plan => {
            listaPlanesDetalles += `
              <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
                <strong>${plan.descripcion}</strong><br>
                <small>ID: ${plan.id} | SubFamilia: ${plan.subfamilia} | Valor: $${plan.valor}</small>
              </div>
            `
          })
          
          if (planesActivosDetalle.length > 5) {
            listaPlanesDetalles += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivosDetalle.length - 5} plan${planesActivosDetalle.length - 5 > 1 ? 'es' : ''} más</small></div>`
          }
        }

      const tieneImpacto = subfamiliasVinculadas > 0 || planesVinculados > 0

      this.$swal.fire({
        title: familia.activo ? '¿Eliminar Familia? (Etapa 1)' : '¿Eliminar Familia Permanentemente? (Etapa 2)',
        html: `
          <div class="text-center">
            <p style="font-size: 1.1rem; margin-bottom: 15px;">
              ¿Estás seguro de que deseas ${familia.activo ? 'desactivar' : 'eliminar permanentemente'} la familia <strong>"${familia.nombre}"</strong>?
            </p>
            
            ${familia.activo ? `
              <div class="alert alert-info text-left" style="margin: 15px 0;">
                <i class="nc-icon nc-single-02"></i>
                <strong>🔄 Eliminación por Etapas:</strong>
                <ul style="margin: 8px 0 0 20px;">
                  <li><strong>Etapa 1 (actual):</strong> Eliminación lógica - se desactiva la familia</li>
                  <li><strong>Etapa 2 (posterior):</strong> Eliminación física - se remueve permanentemente</li>
                </ul>
              </div>
            ` : `
              <div class="alert alert-danger text-left" style="margin: 15px 0;">
                <i class="nc-icon nc-simple-remove"></i>
                <strong>⚠️ Eliminación Permanente:</strong>
                <p style="margin: 8px 0;">Esta familia ya está inactiva. Esta acción la eliminará <strong>permanentemente</strong> del sistema.</p>
              </div>
            `}
            
            <div class="alert ${tieneImpacto ? 'alert-danger' : 'alert-warning'} text-left" style="margin: 15px 0;">
              <i class="nc-icon nc-bell-55"></i>
              <strong>${tieneImpacto ? 'ATENCIÓN - Eliminación en Cascada:' : 'Información importante:'}</strong>
              <ul style="margin: 10px 0 0 20px;">
                ${familia.activo ? '<li>La familia será marcada como <strong>inactiva</strong></li>' : '<li>La familia será <strong>eliminada permanentemente</strong></li>'}
                ${mensajeSubfamilias}
                ${mensajePlanes}
                ${!tieneImpacto && familia.activo ? '<li>Esta acción puede ser reversible desde la administración</li>' : ''}
              </ul>
            </div>
            
            ${listaPlanesDetalles ? `
              <div style="margin: 15px 0; max-height: 200px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 8px; padding: 10px;">
                <h6 style="color: #dc3545; font-weight: 600; margin-bottom: 10px; text-align: center;">
                  <i class="nc-icon nc-bell-55"></i> Planes Activos que Bloquean la Eliminación:
                </h6>
                ${listaPlanesDetalles}
              </div>
            ` : ''}
            
            <p class="text-muted" style="font-size: 0.9rem;">
              ID de familia: <strong>${familia.id}</strong>
            </p>
          </div>
        `,
        icon: tieneImpacto ? 'error' : 'warning',
        showCancelButton: true,
        confirmButtonText: familia.activo 
          ? (tieneImpacto ? 'Sí, desactivar todo (Etapa 1)' : 'Sí, desactivar familia (Etapa 1)')
          : 'Sí, eliminar permanentemente (Etapa 2)',
        cancelButtonText: 'Cancelar',
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          container: 'swal-eliminar-familia-cascada'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.eliminarFamilia(familia)
        } else {
          // Limpiar loading si el usuario cancela
          this.loading.eliminar = null
        }
      })
    },

    async eliminarFamilia(familia) {
      let modalPlanesActivosMostrado = false // Variable de control para evitar cerrar el modal
      
      try {
        // Mostrar modal de loading inmediatamente después de la confirmación
        this.$swal.fire({
          title: 'Eliminando Familia...',
          html: `
            <div class="text-center">
              <div class="spinner-border text-danger" style="width: 3rem; height: 3rem;" role="status">
                <span class="sr-only">Eliminando...</span>
              </div>
              <p class="mt-3" style="font-size: 1.1rem;">
                Procesando eliminación de <strong>"${familia.nombre}"</strong>
              </p>
              <p class="text-muted" style="font-size: 0.9rem;">
                Por favor espera mientras se completa la operación...
              </p>
            </div>
          `,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            container: 'swal-loading-eliminar'
          }
        })

        console.log('🗑️ Eliminando familia:', {
          id: familia.id,
          nombre: familia.nombre
        })

        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: '/v1/planes/familia/eliminar' },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: { id: familia.id },
            Body: {}
          }
        })

        console.log('🗑️ Respuesta del servidor:', response.data)

        // Verificar respuesta exitosa según especificación
        if (response.data?.result?.code === 0 && response.data?.result?.httpCode === 200) {
          const familiaEliminada = response.data?.dataOut?.familia
          const mensaje = response.data?.dataOut?.mensaje || 'Familia eliminada exitosamente'
          
          // Extraer información sobre subfamilias desactivadas del mensaje
          let subfamiliasDesactivadas = 0
          const matchSubfamilias = mensaje.match(/(\d+)\s+subfamilias?\s+también\s+fueron?\s+desactivadas?/i)
          if (matchSubfamilias) {
            subfamiliasDesactivadas = parseInt(matchSubfamilias[1])
          }
          
          // Crear mensaje detallado para el usuario
          let mensajeDetallado = `
            <div class="text-center">
              <i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>
              <h5 style="color: #28a745; margin-bottom: 15px;">¡Etapa 1 Completada!</h5>
              
              <div class="alert alert-success text-left" style="margin: 15px 0;">
                <strong>✅ Familia desactivada:</strong>
                <ul style="margin: 5px 0 0 20px;">
                  <li>"${familiaEliminada ? familiaEliminada.nombre : familia.nombre}" (ID: ${familiaEliminada ? familiaEliminada.id : familia.id})</li>
                </ul>
          `
          
          if (subfamiliasDesactivadas > 0) {
            mensajeDetallado += `
                <strong>🔗 Eliminación en cascada:</strong>
                <ul style="margin: 5px 0 0 20px;">
                  <li><strong>${subfamiliasDesactivadas} subfamilia${subfamiliasDesactivadas > 1 ? 's' : ''}</strong> también ${subfamiliasDesactivadas > 1 ? 'fueron desactivadas' : 'fue desactivada'}</li>
                </ul>
            `
          }
          
          mensajeDetallado += `
              </div>
              
              <div style="margin-top: 15px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 4px;">
                <small class="text-muted">
                  <strong>💡 Etapa 1 Completada:</strong> Eliminación lógica exitosa. Los registros se conservan en la base de datos pero están marcados como inactivos.
                </small>
              </div>
              
              <div style="margin-top: 10px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px; border-left: 4px solid #ffc107;">
                <small class="text-muted">
                  <strong>🔄 Próximo Paso:</strong> Puedes realizar una segunda eliminación para remover permanentemente la familia del sistema.
                </small>
              </div>
            </div>
          `
          
          await this.$swal.fire({
            title: '',
            html: mensajeDetallado,
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-success',
            toast: false,
            position: 'center',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              container: 'swal-eliminacion-exitosa'
            }
          })

          // Recargar lista de familias y actualizar estadísticas DESPUÉS de que el usuario cierre el modal
          await this.cargarFamilias()
          this.$emit('refresh')
          
        } else if (response.data?.result?.code === 409 && response.data?.result?.httpCode === 409) {
          // Conflicto: Planes activos impiden eliminación física
          const planesActivos = response.data?.dataOut?.planesActivos || []
          const resumen = response.data?.dataOut?.resumen || {}
          const accionesRequeridas = response.data?.dataOut?.accionesRequeridas || []
          
          let planesTotalTexto = `${resumen.totalPlanes || planesActivos.length} plan${(resumen.totalPlanes || planesActivos.length) > 1 ? 'es' : ''}`
          
          // Construir lista de planes activos (máximo 5 para no sobrecargar el modal)
          let listaPlanes = ''
          const planesParaMostrar = planesActivos.slice(0, 5)
          planesParaMostrar.forEach(plan => {
            listaPlanes += `
              <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
                <strong>${plan.descripcion}</strong><br>
                <small>ID: ${plan.id} | SubFamilia: ${plan.subfamilia} | Valor: $${plan.valor}</small>
              </div>
            `
          })
          
          if (planesActivos.length > 5) {
            listaPlanes += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivos.length - 5} plan${planesActivos.length - 5 > 1 ? 'es' : ''} más</small></div>`
          }
          
          // Construir mensaje de acciones requeridas
          let accionesTexto = ''
          accionesRequeridas.forEach(accion => {
            accionesTexto += `<li>${accion.descripcion}</li>`
          })
          
          this.$swal.fire({
            title: 'Eliminación Física Bloqueada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #ffc107; margin-bottom: 15px;"></i>
                <h5 style="color: #856404; margin-bottom: 15px;">¡Planes Activos Detectados!</h5>
                
                <div class="alert alert-warning text-left" style="margin: 15px 0;">
                  <strong>⚠️ No se puede eliminar permanentemente:</strong>
                  <p style="margin: 8px 0;">La familia tiene <strong>${planesTotalTexto} activo${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''}</strong> que debe${(resumen.totalPlanes || planesActivos.length) > 1 ? 'n' : ''} ser desactivado${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''} primero.</p>
                </div>
                
                <div style="margin-top: 15px; max-height: 200px; overflow-y: auto;">
                  <h6 style="color: #856404;">PLANES QUE REQUIEREN ATENCIÓN:</h6>
                  ${listaPlanes}
                </div>
                
                ${accionesTexto ? `
                  <div class="alert alert-info text-left" style="margin: 15px 0;">
                    <strong>📋 Acciones requeridas:</strong>
                    <ul style="margin: 8px 0 0 20px;">
                      ${accionesTexto}
                    </ul>
                  </div>
                ` : ''}
                
                <div style="margin-top: 15px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px;">
                  <small class="text-muted">
                    <strong>💡 Sugerencia:</strong> Desactiva o reasigna los planes listados, luego intenta eliminar la familia nuevamente.
                  </small>
                </div>
              </div>
            `,
            icon: 'warning',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-warning',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              container: 'swal-planes-activos'
            }
          })
          
          // Recargar la lista para reflejar cualquier cambio
          await this.cargarFamilias()
          modalPlanesActivosMostrado = true // Marcar que se mostró el modal
          
        } else if (response.data?.result?.code === 500) {
          // Error específico: Familia no encontrada
          const errorMessage = response.data.result.message || 
                             response.data.result.errors?.[0] || 
                             'Familia no encontrada'
          
          this.$swal.fire({
            title: 'Familia No Encontrada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #ff6b6b; margin-bottom: 15px;"></i>
                <p style="font-size: 1.1rem; margin-bottom: 15px;">
                  <strong>${errorMessage}</strong>
                </p>
                <p class="text-muted" style="font-size: 0.9rem;">
                  La familia que intentas eliminar ya no existe en el sistema o fue eliminada previamente.
                </p>
              </div>
            `,
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-danger'
          })
          
          // Recargar la lista para reflejar el estado actual
          await this.cargarFamilias()
          
        } else {
          // Otros errores del servidor
          throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
        }

      } catch (error) {
        console.error('❌ Error al eliminar familia:', error)
        console.log('📊 Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })
        
        let errorMessage = 'No se pudo eliminar la familia'
        let errorTitle = 'Error al Eliminar'
        
        if (error.response?.status === 500) {
          // Error del servidor - familia no encontrada
          errorMessage = error.response.data?.result?.message || 
                        error.response.data?.result?.errors?.[0] ||
                        'Familia no encontrada en el sistema'
          errorTitle = 'Familia No Encontrada'
        } else if (error.response?.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          errorTitle = 'Sesión Expirada'
          // Opcional: redirigir al login
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response?.status === 409) {
          // Conflicto: Planes activos (manejar como caso especial si llega por excepción)
          if (error.response.data?.dataOut?.planesActivos) {
            const planesActivos = error.response.data.dataOut.planesActivos || []
            const resumen = error.response.data.dataOut.resumen || {}
            const accionesRequeridas = error.response.data.dataOut.accionesRequeridas || []
            
            let planesTotalTexto = `${resumen.totalPlanes || planesActivos.length} plan${(resumen.totalPlanes || planesActivos.length) > 1 ? 'es' : ''}`
            
            // Construir lista de planes activos (máximo 5 para no sobrecargar el modal)
            let listaPlanes = ''
            const planesParaMostrar = planesActivos.slice(0, 5)
            planesParaMostrar.forEach(plan => {
              listaPlanes += `
                <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
                  <strong>${plan.descripcion}</strong><br>
                  <small>ID: ${plan.id} | SubFamilia: ${plan.subfamilia} | Valor: $${plan.valor}</small>
                </div>
              `
            })
            
            if (planesActivos.length > 5) {
              listaPlanes += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivos.length - 5} plan${planesActivos.length - 5 > 1 ? 'es' : ''} más</small></div>`
            }
            
            // Construir mensaje de acciones requeridas
            let accionesTexto = ''
            accionesRequeridas.forEach(accion => {
              accionesTexto += `<li>${accion.descripcion}</li>`
            })
            
            this.$swal.fire({
              title: 'Eliminación Física Bloqueada',
              html: `
                <div class="text-center">
                  <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #ffc107; margin-bottom: 15px;"></i>
                  <h5 style="color: #856404; margin-bottom: 15px;">¡Planes Activos Detectados!</h5>
                  
                  <div class="alert alert-warning text-left" style="margin: 15px 0;">
                    <strong>⚠️ No se puede eliminar permanentemente:</strong>
                    <p style="margin: 8px 0;">La familia tiene <strong>${planesTotalTexto} activo${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''}</strong> que debe${(resumen.totalPlanes || planesActivos.length) > 1 ? 'n' : ''} ser desactivado${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''} primero.</p>
                  </div>
                  
                  <div style="margin-top: 15px; max-height: 200px; overflow-y: auto;">
                    <h6 style="color: #856404;">PLANES QUE REQUIEREN ATENCIÓN:</h6>
                    ${listaPlanes}
                  </div>
                  
                  ${accionesTexto ? `
                    <div class="alert alert-info text-left" style="margin: 15px 0;">
                      <strong>📋 Acciones requeridas:</strong>
                      <ul style="margin: 8px 0 0 20px;">
                        ${accionesTexto}
                      </ul>
                    </div>
                  ` : ''}
                  
                  <div style="margin-top: 15px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px;">
                    <small class="text-muted">
                      <strong>💡 Sugerencia:</strong> Desactiva o reasigna los planes listados, luego intenta eliminar la familia nuevamente.
                    </small>
                  </div>
                </div>
              `,
              icon: 'warning',
              showConfirmButton: true,
              confirmButtonText: 'Entendido',
              confirmButtonClass: 'btn btn-warning',
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                container: 'swal-planes-activos'
              }
            })
            modalPlanesActivosMostrado = true // Marcar que se mostró el modal
            return // Salir aquí para no mostrar el modal de error genérico
          } else {
            errorMessage = 'Conflicto al eliminar la familia. Puede tener dependencias activas.'
            errorTitle = 'Conflicto de Eliminación'
          }
        } else if (error.response?.status === 403) {
          errorMessage = 'No tienes permisos para eliminar esta familia.'
          errorTitle = 'Acceso Denegado'
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.loading.eliminar = null
        // Solo cerrar el modal de loading si NO se mostró el modal de planes activos
        if (!modalPlanesActivosMostrado) {
          this.$swal.close() // Cerrar el modal de loading
        }
      }
    },

    async verDetalle(familia) {
      try {
        this.loading.verDetalle = familia.id
        
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: '/v1/planes/familia/consultar-por-id' },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: { id: familia.id },
            Body: {}
          }
        })

        if (response.data?.dataOut?.familia) {
          const familiaDetalle = response.data.dataOut.familia
          
          this.$swal.fire({
            title: familiaDetalle.nombre,
            html: '<div class="text-left">' +
              '<p><strong>ID:</strong> ' + familiaDetalle.id + '</p>' +
              '<p><strong>Estado:</strong> ' + (familiaDetalle.activo ? 'Activo' : 'Inactivo') + '</p>' +
              '<p><strong>Subfamilias vinculadas:</strong> ' + (familiaDetalle.estadisticas?.subfamiliasVinculadas || 0) + '</p>' +
              '<p><strong>Planes vinculados:</strong> ' + (familiaDetalle.estadisticas?.planesVinculados || 0) + '</p>' +
              '<p><strong>Creado por:</strong> ' + familiaDetalle.nombreUsuarioCreacion + '</p>' +
              '<p><strong>Fecha de creación:</strong> ' + this.formatearFecha(familiaDetalle.createdAt) + '</p>' +
              '</div>',
            confirmButtonText: 'Cerrar',
            width: '500px'
          })
        }

      } catch (error) {
        console.error('Error al consultar detalle:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo consultar el detalle de la familia',
          icon: 'error'
        })
      } finally {
        this.loading.verDetalle = null
      }
    },

    async exportarExcel() {
      try {
        this.loading.exportar = true
        
        this.$swal.fire({
          title: 'Exportando...',
          text: 'Generando archivo Excel con todas las familias',
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading()
          }
        })

        // Aquí implementarías la lógica de exportación
        // Por ahora simular con timeout
        setTimeout(() => {
          this.$swal.fire({
            title: '¡Exportado!',
            text: 'El archivo Excel se ha descargado exitosamente',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false
          })
          this.loading.exportar = false
        }, 2000)

      } catch (error) {
        console.error('Error al exportar:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo exportar el archivo Excel',
          icon: 'error'
        })
        this.loading.exportar = false
      }
    },

    // Utils
    formatearFecha(fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(',', ' -')
    },

    mostrarModal() {
      this.$nextTick(() => {
        const modalElement = this.$refs.modalFamilia;
        if (modalElement) {
          if (window.bootstrap && window.bootstrap.Modal) {
            // Bootstrap 5 nativo
            const modal = new window.bootstrap.Modal(modalElement);
            modal.show();
          } else {
            // Fallback manual con JavaScript puro
            modalElement.style.display = 'block';
            modalElement.classList.add('show');
            document.body.classList.add('modal-open');
            
            // Crear backdrop manualmente
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = 'modal-backdrop-familia';
            document.body.appendChild(backdrop);
          }
        }
      });
    },

    cerrarModal() {
      const modalElement = this.$refs.modalFamilia;
      if (modalElement) {
        if (window.bootstrap && window.bootstrap.Modal) {
          // Bootstrap 5 nativo
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        } else {
          // Fallback manual con JavaScript puro
          modalElement.style.display = 'none';
          modalElement.classList.remove('show');
          document.body.classList.remove('modal-open');
          
          // Remover backdrop manual
          const backdrop = document.getElementById('modal-backdrop-familia');
          if (backdrop) {
            backdrop.remove();
          }
        }
      }
      
      // Limpiar formulario
      this.formFamilia = {
        nombre: '',
        actualizarPlanes: false
      };
    }
  }
}
</script>

<style scoped>
.familias-manager {
  padding: 20px;
}

.manager-header {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.manager-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.filters-section .card,
.table-section .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.btn-sort {
  background: none;
  border: none;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-sort:hover {
  color: #667eea;
}

.empty-icon {
  font-size: 4rem;
  color: #dee2e6;
}

.pagination-section .pagination {
  margin-bottom: 0;
}

.pagination-section .page-link {
  border-radius: 6px;
  margin: 0 2px;
  border: 1px solid #dee2e6;
}

.pagination-section .page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

.badge-outline-primary {
  color: #667eea;
  border: 1px solid #667eea;
  background-color: transparent;
}

.familia-info strong {
  color: #2c3e50;
}

.empty-state {
  padding: 3rem 1rem;
}

.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.modal-header {
  border-bottom: 1px solid #f0f0f0;
  border-radius: 12px 12px 0 0;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

/* Estilos para navegación a SubFamilias */
.familia-nombre-clickeable {
  cursor: pointer;
  color: #667eea;
  transition: color 0.3s ease;
}

.familia-nombre-clickeable:hover {
  color: #764ba2;
  text-decoration: underline;
}

.subfamilias-link {
  cursor: pointer;
  color: #28a745;
  transition: color 0.3s ease;
}

.subfamilias-link:hover {
  color: #1e7e34;
  text-decoration: underline;
}

/* Estilos específicos para el modal de familia duplicada */
:deep(.swal-familia-duplicada) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
}

:deep(.swal-familia-duplicada) .swal2-title {
  color: #f39c12;
  font-weight: 700;
}

:deep(.swal-familia-duplicada) .swal2-html-container {
  margin: 1.5rem 0;
}

:deep(.swal-familia-duplicada) .swal2-confirm {
  background: #f39c12 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

:deep(.swal-familia-duplicada) .swal2-confirm:hover {
  background: #e67e22 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(243, 156, 18, 0.4);
}

/* Estilos para modal de familia exitosa */
:deep(.swal-familia-exitosa) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
}

:deep(.swal-familia-exitosa) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-familia-exitosa) .alert-success {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  margin: 15px 0;
  border-radius: 8px;
}

:deep(.swal-familia-exitosa) h5 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
}

:deep(.swal-familia-exitosa) .swal2-confirm {
  background: #28a745 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

:deep(.swal-familia-exitosa) .swal2-confirm:hover {
  background: #218838 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
}

/* Estilos para modales de eliminación */
:deep(.swal-eliminar-familia-cascada) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
}

:deep(.swal-eliminar-familia-cascada) .swal2-title {
  color: #dc3545;
  font-weight: 700;
}

:deep(.swal-eliminar-familia-cascada) .swal2-html-container {
  margin: 1.5rem 0;
  text-align: left;
}

:deep(.swal-eliminar-familia-cascada) .alert-danger {
  border-color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

:deep(.swal-eliminacion-exitosa) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 550px;
}

:deep(.swal-eliminacion-exitosa) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-eliminacion-exitosa) .alert-success {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  margin: 15px 0;
}

:deep(.swal-eliminacion-exitosa) h5 {
  text-align: center;
  margin-bottom: 20px;
}

:deep(.swal-eliminacion-exitosa) .swal2-confirm {
  background: #28a745 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
}

:deep(.swal-eliminacion-exitosa) .swal2-timer-progress-bar {
  background: rgba(40, 167, 69, 0.8) !important;
}

/* Estilos para modal de loading de eliminación */
:deep(.swal-loading-eliminar) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 500px;
}

:deep(.swal-loading-eliminar) .swal2-title {
  color: #dc3545;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-loading-eliminar) .swal2-html-container {
  margin: 0;
  padding: 0;
}

:deep(.swal-loading-eliminar) .spinner-border {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos para modal de planes activos */
:deep(.swal-planes-activos) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 650px;
}

:deep(.swal-planes-activos) .swal2-title {
  color: #856404;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-planes-activos) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-planes-activos) .alert-warning {
  border-color: #ffc107;
  background-color: rgba(255, 193, 7, 0.1);
  margin: 15px 0;
}

:deep(.swal-planes-activos) .alert-info {
  border-color: #17a2b8;
  background-color: rgba(23, 162, 184, 0.1);
  margin: 15px 0;
}

:deep(.swal-planes-activos) h5 {
  text-align: center;
  margin-bottom: 20px;
}

:deep(.swal-planes-activos) h6 {
  color: #856404;
  font-weight: 600;
  margin-bottom: 10px;
}

:deep(.swal-planes-activos) .swal2-confirm {
  background: #ffc107 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  color: #212529 !important;
}

/* Responsive para modales */
@media (max-width: 768px) {
  :deep(.swal-eliminar-familia-cascada) .swal2-popup,
  :deep(.swal-eliminacion-exitosa) .swal2-popup {
    max-width: 90%;
    margin: 10px;
    padding: 1.5rem;
  }
  
  :deep(.swal-eliminar-familia-cascada) .swal2-html-container ul,
  :deep(.swal-eliminacion-exitosa) .swal2-html-container ul {
    padding-left: 15px;
  }
}
</style> 